import styled, { rem } from '@nobia/zeus-components/lib/styled'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'

const Wrapper = styled.div`
  touch-action: none;
  user-select: none;
`

const Label = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #e0e4f1;
  cursor: pointer;
  height: ${rem(29)};
  min-width: ${(p) => rem(p.width)};
  width: ${(p) => rem(p.width)};
  border-radius: ${(p) => rem(p.width)};
  transition: background-color 0.25s;
`

const Button = styled.span`
  content: '';
  position: absolute;
  top: 3px;
  background-color: #fff;
  width: ${(p) => rem(p.size)};
  height: ${(p) => rem(p.size)};
  border-radius: ${(p) => rem(p.size)};
  transform: ${(p) => `translateX(${p.position}px)`};
  transition: transform 0.25s;
`

const Input = styled.input`
  height: 0;
  width: 0;
  display: none;

  &:checked + ${Label} {
    background-color: #71d671;
  }
`

export const Switch = ({ id, checked, onChange }) => {
  const labelWidth = 60
  const buttonSize = 23
  const spacing = 4

  const buttonRef = useRef(null)

  // eslint-disable-next-line no-use-before-define
  const { position, lastDragAt } = useDraggable(buttonRef, {
    minPosX: spacing,
    maxPosX: labelWidth - buttonSize - spacing,
    checked,
    onChange,
  })

  const handleChange = useCallback(() => {
    if (Date.now() - lastDragAt > 50) {
      onChange(!checked)
    }
  }, [lastDragAt, onChange, checked])

  return (
    <Wrapper>
      <Input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
      />
      <Label
        htmlFor={id}
        width={labelWidth}
        onMouseDown={(event) => event.preventDefault()}
      >
        <Button
          ref={buttonRef}
          size={buttonSize}
          position={position}
          onClick={(event) => event.preventDefault()}
        />
      </Label>
    </Wrapper>
  )
}

Switch.defaultProps = {
  checked: false,
  onChange: null,
}

Switch.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
}

const useDraggable = (ref, { checked, minPosX, maxPosX, onChange }) => {
  const [position, setPosition] = useState(checked ? maxPosX : minPosX)
  const [isMouseDown, setIsMouseDown] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartX, setDragStartX] = useState(minPosX)
  const [dragStartedAt, setDragStartedAt] = useState(null)
  const [lastDragAt, setLastDragAt] = useState(0)

  useEffect(
    () => setPosition(checked ? maxPosX : minPosX),
    [checked, maxPosX, minPosX]
  )

  const handleDrag = useCallback(
    (clientX) => {
      const mousePosX = (checked ? maxPosX : minPosX) + clientX - dragStartX
      if (!isDragging && clientX !== dragStartX) {
        setIsDragging(true)
      }
      const nextPosX = Math.min(maxPosX, Math.max(minPosX, mousePosX))
      if (nextPosX !== position) {
        setPosition(nextPosX)
      }
    },
    [checked, maxPosX, minPosX, dragStartX, isDragging, position]
  )

  const handleDragStart = useCallback((clientX) => {
    setDragStartX(clientX)
    setDragStartedAt(Date.now())
  }, [])

  const handleDragStop = useCallback(() => {
    const center = (maxPosX + minPosX) / 2
    setPosition(checked ? maxPosX : minPosX)
    const timeSinceStart = Date.now() - dragStartedAt
    const simulateChange = !isDragging || timeSinceStart < 250
    const changed =
      (checked && position <= center) || (!checked && position >= center)
    if (simulateChange || changed) {
      onChange(!checked)
    }
    setIsDragging(false)
    setLastDragAt(Date.now())
  }, [checked, maxPosX, minPosX, dragStartedAt, position, isDragging, onChange])

  const handleTouchStart = useCallback(
    (event) => handleDragStart(event.touches[0].clientX),
    [handleDragStart]
  )

  const handleTouchMove = useCallback(
    (event) => handleDrag(event.touches[0].clientX),
    [handleDrag]
  )

  const handleTouchEnd = useCallback(
    (event) => {
      event.preventDefault()
      handleDragStop()
    },
    [handleDragStop]
  )

  const handleMouseMove = useCallback(
    (event) => {
      event.preventDefault()
      handleDrag(event.clientX)
    },
    [handleDrag]
  )

  const handleMouseUp = useCallback(
    (event) => {
      handleDragStop(event)
      setIsMouseDown(false)
    },
    [handleDragStop]
  )

  const handleMouseDown = useCallback(
    (event) => {
      event.preventDefault()
      if (event.button !== 0) {
        return
      }
      setIsMouseDown(true)
      handleDragStart(event.clientX)
    },
    [handleDragStart]
  )

  useEffect(() => {
    const current = ref.current
    current.addEventListener('mousedown', handleMouseDown)
    current.addEventListener('touchstart', handleTouchStart)
    current.addEventListener('touchend', handleTouchEnd)
    current.addEventListener('touchmove', handleTouchMove)

    return () => {
      current.removeEventListener('mousedown', handleMouseDown)
      current.removeEventListener('touchstart', handleTouchStart)
      current.removeEventListener('touchend', handleTouchEnd)
      current.removeEventListener('touchmove', handleTouchMove)
    }
  }, [ref, handleMouseDown, handleTouchStart, handleTouchEnd, handleTouchMove])

  useEffect(() => {
    if (isMouseDown) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    } else {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isMouseDown, handleMouseMove, handleMouseUp])

  return { position, lastDragAt }
}
